// Default background when no accent color is set (free/unentitled churches) —
// same brand gradient as Avatar.tsx and the pre-branding ChurchCard tile.
const GRADIENT = 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)'

interface LogoTileProps {
  /** Church logo image, or null/undefined to show the initial fallback. */
  logoUrl?: string | null
  /** Church name — first letter shown when there's no logo. */
  name: string
  /** Solid fallback background when there's no logo; omit for the default brand gradient. */
  accentColor?: string | null
  /** Tile edge length in px. Sizes ≤18 render as a circular "chip" per the design spec. */
  size: number
  /**
   * Set true when this tile sits next to visible text that already names the
   * church inside the same link/button (e.g. a host lockup pill) — the image
   * gets an empty alt so screen readers don't announce the name twice.
   */
  decorative?: boolean
  className?: string
  style?: React.CSSProperties
}

/**
 * The one church-identity tile used everywhere a church logo renders (church
 * card, hosted-by card, event-card host chip, admin Identity/Branding cards).
 * Logo: white tile, `object-fit: contain`, never cropped or distorted.
 * No logo: solid-accent (or default gradient) tile with the church's initial.
 */
export function LogoTile({
  logoUrl,
  name,
  accentColor,
  size,
  decorative,
  className,
  style,
}: LogoTileProps) {
  const isChip = size <= 18
  const borderRadius = isChip ? '50%' : 12
  const padding = Math.round(size * 0.12)

  if (logoUrl) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          borderRadius,
          flexShrink: 0,
          background: '#fff',
          border: '1px solid var(--line-soft)',
          boxSizing: 'border-box',
          padding,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          ...style,
        }}
      >
        <img
          src={logoUrl}
          alt={decorative ? '' : `${name} logo`}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      </div>
    )
  }

  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius,
        flexShrink: 0,
        background: accentColor ?? GRADIENT,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.4,
        fontWeight: 700,
        fontFamily: 'Newsreader, serif',
        ...style,
      }}
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  )
}
